import type { IDoohFilterVenueType } from '@/Interfaces/Forecaster/Dooh.interface';
import type { FilterVenueTypesNode } from '@/types/Forecaster/VenueNode.types';

export function buildVenueTypesFilterTree(venueTypes: IDoohFilterVenueType[]): FilterVenueTypesNode[] {
  if (!venueTypes) {
    return [];
  }
  const map = new Map<string, FilterVenueTypesNode>();

  venueTypes.forEach(venueType => {
    map.set(venueType.id, { ...venueType, children: [] });
  });

  const roots: FilterVenueTypesNode[] = [];

  venueTypes.forEach(venueType => {
    const node = map.get(venueType.id);

    if (!node) {
      return;
    }

    if (venueType.parentId) {
      const parent = map.get(venueType.parentId);
      if (parent) {
        parent.children.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  return roots;
}

export function getAllVenueTypesFilterTreeChildren(node: FilterVenueTypesNode): FilterVenueTypesNode[] {
  const result: FilterVenueTypesNode[] = [];

  const getChildren = (value: FilterVenueTypesNode) => {
    if (!value.children || value.children.length === 0) {
      return;
    }

    for (const child of value.children) {
      result.push(child);
      getChildren(child);
    }
  };

  getChildren(node);

  return result;
}

export function collectVisibleIds(nodes: FilterVenueTypesNode[], search: string): Set<string> {
  const result = new Set<string>();
  const query = search.trim().toLowerCase();

  if (!query) return result;

  const parentMap = new Map<string, string | null>();

  buildParentMap(nodes, null, parentMap);
  collectMatches(nodes, query, parentMap, result);

  return result;
}

function buildParentMap(nodes: FilterVenueTypesNode[], parentId: string | null, map: Map<string, string | null>) {
  for (const node of nodes) {
    map.set(node.id, parentId);
    buildParentMap(node.children, node.id, map);
  }
}

function collectMatches(
  nodes: FilterVenueTypesNode[],
  query: string,
  parentMap: Map<string, string | null>,
  result: Set<string>,
) {
  for (const node of nodes) {
    if (node.name.toLowerCase().includes(query)) {
      addWithParents(node.id, parentMap, result);
    }

    collectMatches(node.children, query, parentMap, result);
  }
}

function addWithParents(id: string, parentMap: Map<string, string | null>, result: Set<string>) {
  let current: string | null | undefined = id;

  while (current) {
    if (result.has(current)) break;
    result.add(current);
    current = parentMap.get(current);
  }
}
